<script>
import {BCard, BCardTitle, BFormGroup, BFormInput, BFormTextarea, BFormDatepicker, BFormCheckbox,
    BRow, BCol, BTabs, BTab
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {required} from '@validations'
import vSelect from 'vue-select'
import {i18nApi, LegislationApi, SubstanceApi} from "@core/api";
import {VerificationDocumentType} from "@/libs/product";
import {RegulationType} from "@core/domain/legislation";

export default {
    props: {
        legislation: {
            type: Object
        }
    },
    components: {
        BCard, BCardTitle, BFormGroup, BFormInput, BFormTextarea, BFormDatepicker, ValidationProvider, ValidationObserver,
        BRow, BCol, required, vSelect, BTabs, BTab, BFormCheckbox
    },
    data() {
        return {
            verificationDocumentTypeOptions: [],

            validFrom: null,
            validTo: null,
            regulationId: null,
            systemKey: this.legislation.key,
            coveredWeight: null,
            regulationType: null,
            status: 'draft',
            requirements: {
                requiresProductWeight: null
            },

            verificationDocumentTypes: [],
            mandatoryVerificationDocumentTypes: [],
            substanceList: this.legislation.substanceList,
            substanceListCollection: [],
            regulationTypeOptions: RegulationType.all().map(type => {
                return {id: type, title: this.$t(RegulationType.translationKey(type))};
            }),
            statusOptions: ['published', 'draft'],
            languages: [],
        };
    },
    methods: {
        emit() {
            this.$emit('input', {
                validFrom: this.validFrom,
                validTo: this.validTo,
                regulationId: this.regulationId,
                regulationType: this.regulationType ? this.regulationType.id : null,
                substanceList: this.substanceList,
                coveredWeight: this.coveredWeight,
                verificationDocumentTypes: this.verificationDocumentTypes.map(t => t.id),
                mandatoryVerificationDocumentTypes: this.mandatoryVerificationDocumentTypes.map(t => t.id),
                key: this.systemKey,
                status: this.status,
                requirements: this.requirements
            })
        },

        verificationDocumentTypesChange() {
            for (let i = 0; i < this.mandatoryVerificationDocumentTypes.length; ++i) {
                if (!this.verificationDocumentTypes.find(d => d.id === this.mandatoryVerificationDocumentTypes[i].id)) {
                    this.mandatoryVerificationDocumentTypes.splice(i, 1);
                    break;
                }
            }

            this.emit();
        },

        transformVerificationDocumentTypes(types) {
            return types.map(type => {
                return {
                    id: type,
                    title: this.$t(VerificationDocumentType.translationKey(type))
                }
            });
        }
    },
    computed: {
        //  by reference
        translations() {
            return this.legislation.translations || null;
        },
    },
    watch: {
        legislation() {
            this.title = this.legislation.title;
            this.status = this.legislation.status;
            this.shortTitle = this.legislation.shortTitle;
            this.systemKey = this.legislation.key;
            this.description = this.legislation.description;
            this.validFrom = this.legislation.validFrom;
            this.validTo = this.legislation.validTo;
            this.regulationId = this.legislation.regulationId;
            this.regulationType = {
                id: this.legislation.regulationType,
                title: this.$t('Component(RegulationTypeEnum):' + this.legislation.regulationType)
            };
            this.coveredWeight = this.legislation.coveredWeight;
            this.substanceList = this.legislation.substanceList;
            this.verificationDocumentTypes = this.transformVerificationDocumentTypes(this.legislation.verificationDocumentTypes);
            this.mandatoryVerificationDocumentTypes = this.transformVerificationDocumentTypes(this.legislation.mandatoryVerificationDocumentTypes);
            this.requirements = this.legislation.requirements;
        },
    },
    created() {
        i18nApi.getLanguages().then(r => {
            this.languages = r.data
        });
        SubstanceApi.lists().then(r => this.substanceListCollection = r.data);
        LegislationApi
            .getVerificationDocumentTypes()
            .then(r => {
                this.verificationDocumentTypeOptions = this.transformVerificationDocumentTypes(r.data);
            })
    }
}
</script>

<template>
    <b-card no-body>
        <div class="card-header pb-1">
            <div class="col-md-8 pl-0">
                <b-card-title>
                    {{ $t('Component(Text):general-info') }}
                </b-card-title>
            </div>
            <div class="col-md-4">
                <b-form-group
                    label="Status"
                    label-for="status"
                    class="mb-0 pb-0"
                >
                    <v-select
                        v-model="status"
                        @input="emit"
                        id="status"
                        label="status"
                        :clearable="false"
                        :options="statusOptions"
                    />
                </b-form-group>
            </div>
        </div>
        <div class="pl-2 pr-2 pb-1">
            <validation-observer ref="generalInfo">
                <b-tabs v-if="translations">
                    <b-tab
                        v-for="language in languages"
                        :title="language.code.toUpperCase()"
                    >
                        <b-row>
                            <b-col>
                                <b-form-group
                                    :label="$t('Component(Label):name')"
                                    :label-for="'name' + language.code"
                                >
                                    <validation-provider
                                        #default="{ errors }"
                                        :name="$t('Component(Label):name')"
                                        rules="required"
                                    >
                                        <b-form-input
                                            :state="errors.length > 0 ? false:null"
                                            :id="'name' + language.code"
                                            v-model="translations[language.code].title"
                                            @input="emit"
                                            :placeholder="$t('Component(Placeholder):type-here')"
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                </b-form-group>
                            </b-col>
                            <b-col>
                                <b-form-group
                                    :label="$t('Component(Label):short-name')"
                                    :label-for="'shortName' + language.code"
                                >
                                    <validation-provider
                                        #default="{ errors }"
                                        :name="$t('Component(Label):short-name')"
                                        rules="required"
                                    >
                                        <b-form-input
                                            :state="errors.length > 0 ? false:null"
                                            :id="'shortName' + language.code"
                                            v-model="translations[language.code].shortTitle"
                                            @input="emit"
                                            :placeholder="$t('Component(Placeholder):type-here')"
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                </b-form-group>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <b-form-group
                                    :label="$t('Component(Label):description')"
                                    label-for="description"
                                    :label-for="'description' + language.code"
                                >
                                    <b-form-textarea
                                        :id="'description' + language.code"
                                        v-model="translations[language.code].description"
                                        rows="2"
                                        @input="emit"
                                        :placeholder="$t('Component(Placeholder):description')"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <b-row>
                                    <b-col>
                                        <b-form-group
                                            label="Legislation Information header"
                                            :label-for="'legislation-info-header' + language.code"
                                        >
                                            <b-form-input
                                                :id="'legislation-info-header' + language.code"
                                                v-model="translations[language.code].legislationInfoHeader"
                                                @input="emit"
                                                :placeholder="$t('Component(Placeholder):type-here')"
                                            />
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                                <b-row>
                                    <b-col>
                                        <b-form-group
                                            label="Legislation Information"
                                            :label-for="'legislation-info-description' + language.code"
                                        >
                                            <b-form-textarea
                                                :id="'legislation-info-description' + language.code"
                                                v-model="translations[language.code].legislationInfo"
                                                rows="2"
                                                @input="emit"
                                                :placeholder="$t('Component(Placeholder):type-here')"
                                            />
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                            </b-col>
                            <b-col>
                                <b-form-group
                                    label="Substance List information"
                                    :label-for="'list-info-description' + language.code"
                                >
                                    <b-form-textarea
                                        :id="'list-info-description' + language.code"
                                        v-model="translations[language.code].listInfo"
                                        rows="5"
                                        @input="emit"
                                        :placeholder="$t('Component(Placeholder):type-here')"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-tab>
                </b-tabs>
                <b-row>
                    <b-col md="6" lg="6">
                        <b-row>
                            <b-col>
                                <b-form-group
                                    :label="$t('Component(Label):valid-from')"
                                    label-for="valid-from"
                                >
                                    <b-form-datepicker
                                        id="valid-from"
                                        v-model="validFrom"
                                        :placeholder="$t('Component(Placeholder):valid-from')"
                                        :date-format-options="$store.state.component.datePicker.format"
                                        :locale="$store.state.component.datePicker.locale"
                                        @input="emit"
                                    />
                                </b-form-group>
                            </b-col>

                        </b-row>
                        <b-row>
                            <b-col>
                                <b-form-group
                                    :label="$t('Component(Label):valid-to')"
                                    label-for="valid-to"
                                >
                                    <b-form-datepicker
                                        id="valid-to"
                                        v-model="validTo"
                                        :placeholder="$t('Component(Placeholder):valid-to')"
                                        :date-format-options="$store.state.component.datePicker.format"
                                        :locale="$store.state.component.datePicker.locale"
                                        @input="emit"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-col>
                    <b-col md="6" lg="6">
                        <b-row>
                            <b-col>
                                <b-form-group
                                    :label="$t('Component(Label):regulation-id')"
                                    label-for="regulationId"
                                >
                                    <validation-provider
                                        #default="{ errors }"
                                        :name="$t('Component(Label):regulation-id')"
                                        rules="required"
                                    >
                                        <b-form-input
                                            :state="errors.length > 0 ? false:null"
                                            id="regulationId"
                                            v-model="regulationId"
                                            @input="emit"
                                            :placeholder="$t('Component(Placeholder):type-here')"
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                </b-form-group>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <b-form-group
                                    :label="$t('Component(Label):regulation-type')"
                                    label-for="regulationType"
                                >
                                    <validation-provider
                                        #default="{ errors }"
                                        :name="$t('Component(Label):regulation-type')"
                                        rules="required"
                                    >
                                        <v-select
                                            v-model="regulationType"
                                            :state="errors.length > 0 ? false:null"
                                            @input="emit"
                                            id="regulationType"
                                            label="title"
                                            :clearable="false"
                                            :placeholder="$t('Component(Placeholder):regulation-type')"
                                            :options="regulationTypeOptions"
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-form-group
                            :label="$t('Component(Label):substance-list')"
                            label-for="shortName"
                        >
                            <v-select
                                v-model="substanceList"
                                @input="emit"
                                label="title"
                                :clearable="false"
                                :placeholder="$t('Component(Placeholder):substance-list')"
                                :options="substanceListCollection"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col>
                        <b-row>
                            <b-col>
                                <b-form-group
                                    :label="$t('Component(Label):covered-weight')"
                                    label-for="coveredWeight"
                                >
                                    <b-form-input
                                        id="coveredWeight"
                                        @input="emit"
                                        v-model="coveredWeight"
                                        :placeholder="$t('Component(Placeholder):type-here')"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col>
                                <b-form-group
                                    :label="$t('Component(Label):system-key')"
                                    label-for="systemKey"
                                >
                                    <b-form-input
                                        id="systemKey"
                                        v-model="systemKey"
                                        :disabled="legislation.id > 0"
                                        :placeholder="$t('Component(Placeholder):type-here')"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-form-group
                            :label="$t('Component(Label):verification-documents')"
                            label-for="shortName"
                        >
                            <v-select
                                v-model="verificationDocumentTypes"
                                @input="verificationDocumentTypesChange"
                                label="title"
                                multiple
                                :clearable="false"
                                :placeholder="$t('Component(Placeholder):verification-documents')"
                                :options="verificationDocumentTypeOptions"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-form-group
                            :label="$t('Component(Label):mandatory-verification-documents')"
                            label-for="mandatory-documents"
                        >
                            <v-select
                                v-model="mandatoryVerificationDocumentTypes"
                                @input="emit"
                                id="mandatory-documents"
                                label="title"
                                multiple
                                :clearable="false"
                                :placeholder="$t('Component(Label):mandatory-verification-documents')"
                                :options="verificationDocumentTypes"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-form-checkbox @input="emit" v-model="requirements.requiresProductWeight">{{ $t('Component(Label):requires-product-weight') }}</b-form-checkbox>
                    </b-col>
                </b-row>
            </validation-observer>
        </div>
    </b-card>
</template>
